import { useMemo, useState } from "react"

import { ContentObjectType } from "@vertesia/common"
import { Bookmark } from "lucide-react"
import { toast } from "sonner"

import { ActionTooltipButton } from "@components/buttons/ActionTooltipButton"

import { cn } from "@lib/utils"

import { useUserSession } from "@session/UserSession"

import { Button } from "@ui/button"
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@ui/dialog"
import { Input } from "@ui/input"
import { Label } from "@ui/label"
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from "@ui/select"

const NONE = "NONE"

interface SaveDocumentModalProps {
    content: string | object
    variant?: "default" | "destructive" | "outline" | "secondary" | "ghost" | "link"
    size?: "default" | "sm" | "lg" | "icon" | "xs"
    title?: boolean
    className?: string
}
export function SaveDocumentModal({ content, variant, size, title = false, className }: Readonly<SaveDocumentModalProps>) {
    const { client, typeRegistry } = useUserSession()

    const types = useMemo(() => {
        return typeRegistry?.types || ([] as ContentObjectType[])
    }, [typeRegistry?.types])

    const [open, setOpen] = useState(false)
    const [type, setType] = useState<string>(NONE)
    const [isUploading, setIsUploading] = useState(false)

    const [filterValue, setFilterValue] = useState("")
    const [fileName, setFileName] = useState("")

    const filteredOptions = types.filter((type) => type.name.toLowerCase().includes(filterValue.toLowerCase()))

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        e.stopPropagation()
        setFilterValue(e.target.value)
    }

    const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        e.stopPropagation()
    }

    const handleFileNameInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        e.stopPropagation()
        setFileName(e.target.value)
    }

    const handleOpen = () => {
        setOpen(true)
        setIsUploading(false)
        setType(NONE)
        setFileName("")
    }

    const handleClose = () => {
        setOpen(false)
    }

    const handleOnUpload = () => {
        setIsUploading(true)

        const file = textToFile(content, fileName)

        client.objects
            .create({
                type: type === NONE ? undefined : type,
                content: file,
            })
            .then(() => {
                toast.success("Document uploaded successfully")
                handleClose()
            })
            .catch(() => {
                toast.error("Failed to upload document")
            })
            .finally(() => {
                setIsUploading(false)
            })
    }

    return (
        <>
            <ActionTooltipButton variant={variant || "outline"} size={size || "default"} text={title ? "Save" : undefined} tooltip="Save to Vertesia" icon={<Bookmark />} onClick={handleOpen} className={cn(className)} />
            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Upload Document to Vertesia</DialogTitle>
                    </DialogHeader>
                    <div className="flex flex-col gap-2">
                        <div>
                            <Label>Upload Type</Label>
                            <Select onValueChange={setType} defaultValue={NONE} disabled={isUploading}>
                                <SelectTrigger>
                                    <SelectValue placeholder="Select Document Type" />
                                </SelectTrigger>
                                <SelectContent>
                                    {types && types.length > 5 && (
                                        <div className="p-2">
                                            <Input placeholder="Search Types..." value={filterValue} onChange={handleInputChange} onKeyDown={handleInputKeyDown} onClick={(e) => e.stopPropagation()} />
                                        </div>
                                    )}
                                    <SelectGroup className="max-h-[200px] overflow-y-auto">
                                        <SelectItem value={NONE}>Let Vertesia Choose</SelectItem>
                                        {filteredOptions.map((type) => (
                                            <SelectItem key={type.id} value={type.id}>
                                                {type.name}
                                            </SelectItem>
                                        ))}
                                    </SelectGroup>
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label>File Name</Label>
                            <Input placeholder="File name" value={fileName} onChange={handleFileNameInputChange} onKeyDown={handleInputKeyDown} onClick={(e) => e.stopPropagation()} disabled={isUploading} />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="destructive" onClick={handleClose} disabled={isUploading}>
                            Cancel
                        </Button>
                        <Button variant="outline" onClick={handleOnUpload} disabled={isUploading || !fileName}>
                            {isUploading ? "Uploading..." : "Upload"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    )
}

function textToFile(content: string | object, filename: string) {
    const isJson = isJSON(content)
    const value = isJson ? JSON.stringify(content, null, 2) : String(content)
    const type = isJson ? "application/json" : "text/plain"
    const extension = isJson ? "json" : "txt"

    const blob = new Blob([value], { type: type })

    return new File([blob], `${filename}.${extension}`, { type: type })
}

function isJSON(content: any): boolean {
    return content && typeof content === "object"
}
